import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { get, post } from '../../lib/api.js';

/**
 * `qrcode.react` (~44 KB of the shipped bundle) is loaded only once a session
 * actually reaches the 'secret' phase of the component below — i.e. only for
 * the admin who is genuinely mid-enrollment. Every other session (an
 * already-enrolled admin, which is the common case) never fetches it. This is
 * a plain dynamic `import()`, not a route change: the security-relevant
 * "render in place of the router" gating this component performs is
 * untouched.
 */
const QRCodeSVG = lazy(() => import('qrcode.react').then((m) => ({ default: m.QRCodeSVG })));

/**
 * Two-factor enrollment — the screen that unblocks an invited administrator.
 *
 * `org_admin` requires MFA-strength authentication on every request. Someone who
 * has just accepted an invitation holds a 'standard' session, because that is
 * the truth: they have no second factor yet. Until they enrol one, the server
 * refuses every route that reads tenant data.
 *
 * So this is not a settings screen. It is the whole application for that person
 * until they finish it, which is why it renders in place of the router.
 *
 * IT IS NOT, HOWEVER, A ROOM WITH NO DOOR, and it used to be. Two things made
 * it one, and both were on the DEFAULT path rather than in some corner:
 *
 *   1. THE TRAP. There was no way to dismiss it and no way to sign out. An
 *      administrator who could not finish enrollment — wrong account, no phone
 *      to hand, an authenticator not yet installed — had one working control on
 *      the screen, and it was the one that could not work for them. "Offering
 *      later would hand them an application where nothing works" is true and is
 *      not an argument against a way OUT; it is an argument against a way past.
 *      So there is still no "skip", and there is now always a sign-out.
 *
 *   2. THE IMPOSSIBLE DEMAND. The intro asked, unconditionally, for the account
 *      password. "Continue with Google" is the FIRST control on the accept-
 *      invite screen, above the password option, so the majority of first
 *      administrators arrive here having never set a password — and
 *      `acceptWithFederatedIdentity` does not create one. The screen asked them
 *      to confirm a credential that had never existed, refused them with "that
 *      email and password do not match", and would not let them leave. The
 *      first administrator of an organization locked themselves out by taking
 *      the path the product recommends.
 *
 * WHAT REPLACES THE DEMAND. `GET /auth/mfa/enroll/options` reports which
 * re-authentication doors the DEPLOYMENT can open, and the screen offers those.
 * A federated account re-asserts through Google — the round trip Identity
 * Platform exchanges for the ID token enrollment needs, which is the only way a
 * federated administrator can reach 'mfa' strength at all (see
 * `signInWithGoogleIdToken` in gcip.ts). A password account confirms its
 * password, exactly as before. Neither is guessed at from account state that
 * nothing on the request path can read honestly.
 *
 * Enrollment itself is a QR code, not a string to retype. The secret is a
 * 32-character base32 key; asking somebody to transcribe it into a phone is
 * slow, easy to get wrong, and the mistake surfaces only as "that code didn't
 * work" with no clue which character was dropped. Every authenticator app
 * scans, so scanning is the normal path and the key sits behind a disclosure
 * for the cases that cannot — a desktop authenticator, a camera that will not
 * focus, a password manager that takes a pasted key.
 *
 * The QR encodes the otpauth:// URI the SERVER builds. It is rendered as inline
 * SVG, so the secret is never written into an <img src>, never fetched from a
 * third-party chart service, and never leaves the page it was delivered to.
 *
 * Phibian never stores the secret — Identity Platform does. It is shown once,
 * over the enrolling person's own authenticated session, and is not recoverable
 * afterwards.
 */

type EnrollMethod = 'google' | 'password';

interface EnrollOptions {
  methods: EnrollMethod[];
  /** A Google re-assertion is already parked server-side for this session. */
  reauthenticated: boolean;
  /**
   * WHY the password door is or is not in `methods`, in the server's own words.
   *
   * `'absent'` — Identity Platform told us, at this identity's last sign-in,
   * that it holds no password credential. The door is withheld because it could
   * not work, not because the deployment lacks it.
   * `'unknown'` — nobody has looked. `methods` is then whatever the deployment
   * can open, exactly as this screen behaved before the account was consulted.
   *
   * The screen does not branch on it today; it is read so that a future message
   * distinguishing "withheld" from "never examined" has the fact to hand rather
   * than inferring it from a shorter list.
   */
  passwordCredential?: 'present' | 'absent' | 'unknown';
}

type Phase =
  | { kind: 'intro' }
  | { kind: 'secret'; sharedSecretKey: string; otpauthUri: string }
  | { kind: 'done' };

/** Where the federated re-assertion round trip starts. A navigation, not an XHR. */
const GOOGLE_REASSERT_PATH = '/auth/mfa/enroll/google/start';

/**
 * @param authnEmail THE ADDRESS THIS SESSION AUTHENTICATED WITH, and nothing
 * else. It used to be `me.email` — `app_user.email`, the per-organization
 * application PROFILE — which is a different record and, for the founder's own
 * staging persona, a different address: he signed in with his real Google
 * account and this screen told him "Signed in as owner@phibian-demo.example"
 * and then asked him to confirm a password for it. That identity is
 * federated-only and holds no password at all.
 *
 * `undefined` when the server did not state one. The screen then names NO
 * address rather than reaching for the profile: an unattributed "Signed in"
 * line is a small loss, and naming the wrong person is the defect.
 */
export function EnrollMfa({ authnEmail }: { readonly authnEmail?: string }) {
  const [phase, setPhase] = useState<Phase>({ kind: 'intro' });
  const [options, setOptions] = useState<EnrollOptions | null>(null);
  const [optionsFailed, setOptionsFailed] = useState(false);
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [showManualKey, setShowManualKey] = useState(false);

  /**
   * Ask the server for the secret, having already re-authenticated one way or
   * the other. Shared by both doors because everything after the door is the
   * same — the branch is entirely about where the ID token came from.
   */
  const beginEnrollment = useCallback(async (body: Record<string, unknown>) => {
    setBusy(true);
    setError(null);
    try {
      const result = await post<{ sharedSecretKey: string; otpauthUri: string }>(
        '/auth/mfa/enroll/start',
        body,
      );
      setPhase({ kind: 'secret', ...result });
      setPassword('');
    } catch (err) {
      // A re-assertion that expired between the redirect and this call is not a
      // failure to explain away — it is a round trip to offer again.
      const reason = (err as { body?: { reason?: string } }).body?.reason;
      setError(
        reason === 'reauthentication_required'
          ? 'That confirmation expired. Confirm it is you again to continue.'
          : (err as Error).message || 'We could not start setup.',
      );
      setPhase({ kind: 'intro' });
    } finally {
      setBusy(false);
    }
  }, []);

  /**
   * Which doors exist, and whether the person has just come back through one.
   *
   * Runs once, on mount. The failure branch matters as much as the success one:
   * if this call cannot be made, the screen must still render the sign-out
   * rather than becoming a blank room — which is the whole defect this screen
   * was rebuilt around.
   */
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const loaded = await get<EnrollOptions>('/auth/mfa/enroll/options');
        if (cancelled) return;
        const methods = Array.isArray(loaded.methods) ? loaded.methods : [];
        setOptions({
          methods,
          reauthenticated: loaded.reauthenticated === true,
          ...(loaded.passwordCredential ? { passwordCredential: loaded.passwordCredential } : {}),
        });
        // Just back from Google, holding a re-assertion that expires in
        // minutes. Spending it immediately is both kinder and safer than
        // parking it behind another button.
        if (loaded.reauthenticated === true) await beginEnrollment({ method: 'google' });
      } catch {
        if (!cancelled) setOptionsFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [beginEnrollment]);

  const startWithPassword = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      await beginEnrollment({ method: 'password', password });
    },
    [beginEnrollment, password],
  );

  const finish = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setBusy(true);
      setError(null);
      try {
        await post('/auth/mfa/enroll/finish', { code: code.trim() });
        setPhase({ kind: 'done' });
        // The session was re-minted at MFA strength server-side. A full reload
        // is what picks it up — the app must boot from a fresh /auth/me.
        window.location.assign('/');
      } catch (err) {
        setError((err as Error).message || 'That code was not accepted.');
        setBusy(false);
      }
    },
    [code],
  );

  /**
   * The door out.
   *
   * Not a "skip": it ends the session rather than dismissing the requirement,
   * so the account is exactly as unprivileged afterwards as the server already
   * believed it to be. The reload is what returns them to sign-in.
   *
   * The navigation happens whether or not the logout call succeeded. A person
   * trying to leave a screen they cannot use must not be held on it by a failed
   * request — the server-side session is the authority either way, and the
   * worst case of navigating anyway is that they land back here.
   */
  const signOut = useCallback(async () => {
    setSigningOut(true);
    try {
      await post('/auth/logout', {});
    } catch {
      // Deliberately swallowed; see above.
    }
    window.location.assign('/');
  }, []);

  const methods = options?.methods ?? [];
  const googleAvailable = methods.includes('google');
  const passwordAvailable = methods.includes('password');
  // Nothing to offer: either the options call failed, or the deployment has no
  // identity provider configured. Say so plainly rather than rendering a form
  // that cannot work.
  const nothingOffered =
    phase.kind === 'intro' && !busy && options !== null && methods.length === 0;

  return (
    <main className="signin-panel">
      <div className="signin-wrap">
        <div className="signin-card">
          <h1 className="signin-h2">Set up two-factor authentication</h1>

          {phase.kind === 'intro' ? (
            <>
              <p className="signin-sub">
                Administrator accounts need a second factor before they can manage an organization,
                so this is the last step before you&rsquo;re in. You&rsquo;ll need an authenticator
                app — Google Authenticator, 1Password, Authy, or any other.
              </p>

              {options === null && !optionsFailed ? (
                <p className="invite-hint">Checking how you can confirm it&rsquo;s you…</p>
              ) : null}

              {googleAvailable ? (
                <>
                  {/* A navigation, not an XHR: the confirmation is a round trip
                      through Google and back. Listed first because it is the
                      door the default invitation path came through, and the
                      only one an account with no password can use. */}
                  <a className="signin-primary" href={GOOGLE_REASSERT_PATH}>
                    Confirm with Google
                  </a>
                  <p className="invite-hint">
                    We ask again because this changes how your account is protected.
                  </p>
                </>
              ) : null}

              {googleAvailable && passwordAvailable ? (
                <div className="invite-divider">
                  <span>or confirm your password</span>
                </div>
              ) : null}

              {passwordAvailable ? (
                <form onSubmit={startWithPassword}>
                  <label className="invite-field">
                    <span className="invite-label">
                      Confirm your password{authnEmail ? ` for ${authnEmail}` : ''}
                    </span>
                    <input
                      className="signin-input"
                      type="password"
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      autoFocus={!googleAvailable}
                    />
                  </label>
                  {googleAvailable ? null : (
                    <p className="invite-hint">
                      We ask again because this changes how your account is protected.
                    </p>
                  )}
                  {error ? (
                    <p className="signin-err" role="alert">
                      {error}
                    </p>
                  ) : null}
                  <button className="signin-primary" type="submit" disabled={busy}>
                    {busy ? 'Starting…' : 'Continue'}
                  </button>
                </form>
              ) : null}

              {/* The error has nowhere else to live when there is no password
                  form to hang it under. */}
              {error && !passwordAvailable ? (
                <p className="signin-err" role="alert">
                  {error}
                </p>
              ) : null}

              {optionsFailed ? (
                <p className="signin-err" role="alert">
                  We couldn&rsquo;t check how to confirm it&rsquo;s you. Reload the page, or sign
                  out and try again.
                </p>
              ) : null}

              {nothingOffered ? (
                <p className="signin-err" role="alert">
                  Two-factor setup isn&rsquo;t available on this deployment yet, so this account
                  can&rsquo;t finish signing in. Ask whoever invited you to contact support — you
                  can sign out below in the meantime.
                </p>
              ) : null}
            </>
          ) : null}

          {phase.kind === 'secret' ? (
            <>
              <p className="signin-sub">
                Scan this QR code with Google Authenticator, Microsoft Authenticator, 1Password, or
                another authenticator app.
              </p>

              <div className="mfa-qr">
                {/* Inline SVG, not an <img>: the secret is encoded in the markup
                    this page already holds, so it is never placed in a URL and
                    never fetched from anywhere. */}
                <Suspense fallback={null}>
                  <QRCodeSVG
                    value={phase.otpauthUri}
                    size={188}
                    level="M"
                    marginSize={2}
                    title="Two-factor setup QR code"
                  />
                </Suspense>
              </div>

              {/* A phone browsing the admin directly can hand the URI straight
                  to its authenticator rather than scanning its own screen. */}
              <p className="invite-hint mfa-qr-hint">
                On this device? <a href={phase.otpauthUri}>Open your authenticator app</a>
              </p>

              {showManualKey ? (
                <div className="signin-org mfa-manual" style={{ display: 'block' }}>
                  <div className="signin-org-label">Setup key</div>
                  <code className="mfa-manual-key">{phase.sharedSecretKey}</code>
                  <button
                    type="button"
                    className="signin-change"
                    style={{ marginTop: 8 }}
                    onClick={() => {
                      void navigator.clipboard
                        ?.writeText(phase.sharedSecretKey)
                        .then(() => setCopied(true))
                        .catch(() => setCopied(false));
                    }}
                  >
                    {copied ? 'Copied' : 'Copy key'}
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="signin-change mfa-manual-toggle"
                  onClick={() => setShowManualKey(true)}
                >
                  Can&rsquo;t scan the QR code? Enter setup key manually.
                </button>
              )}

              <form onSubmit={finish}>
                <label className="invite-field">
                  <span className="invite-label">Enter the 6-digit code from your app</span>
                  <input
                    className="signin-input"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    required
                    autoFocus
                  />
                </label>
                {error ? (
                  <p className="signin-err" role="alert">
                    {error}
                  </p>
                ) : null}
                <button className="signin-primary" type="submit" disabled={busy}>
                  {busy ? 'Verifying…' : 'Turn on two-factor'}
                </button>
              </form>

              {/* A way back, not a way past. The secret shown above is only ever
                  shown once, so somebody who scanned it into the wrong account
                  or lost the phone mid-flow needs to be able to ask for another
                  one rather than being stuck against a code they cannot produce. */}
              <button
                type="button"
                className="signin-change"
                onClick={() => {
                  setError(null);
                  setCode('');
                  setShowManualKey(false);
                  setPhase({ kind: 'intro' });
                }}
              >
                Start over
              </button>
            </>
          ) : null}

          {phase.kind === 'done' ? <p className="signin-sub">Setting up your workspace…</p> : null}

          {/* THE DOOR OUT, and it is rendered in every phase including the
              loading one. A screen that renders in place of the router with no
              way to leave is a lockout however good its reason, and the moment
              it is conditional is the moment some state loses it. */}
          <div className="signin-support mfa-signout">
            {authnEmail ? (
              <>
                Signed in as <strong>{authnEmail}</strong>.{' '}
              </>
            ) : null}
            Not you, or can&rsquo;t finish right now?{' '}
            <button
              type="button"
              className="signin-change"
              onClick={() => void signOut()}
              disabled={signingOut}
            >
              {signingOut ? 'Signing out…' : 'Sign out'}
            </button>
          </div>
        </div>

        <div className="signin-support">
          Stuck? Ask whoever invited you — they can check your account.
        </div>
      </div>
    </main>
  );
}
